// Copyright (c) 2023 WiseTime. All rights reserved.

import ColorHash from "color-hash"

export type ColorAssignment = {
  id: string,
  color: GeneratedColor,
}

type GeneratedColor = {
  hsl: number[],
  hex: HexColor,
}

export type HexColor = string

const baselineSaturation = 0.35
const baselineLightness = 0.85

export const colorFromId = (id: string, options?: { saturation: number, lightness: number }): GeneratedColor => {
  const config = options !== undefined
    ? options
    : { saturation: baselineSaturation, lightness: baselineLightness }
  const reverse = (id: string): string => id.split("").reverse().join("")
  return ({
    hsl: new ColorHash(config).hsl(reverse(id)),
    hex: new ColorHash(config).hex(reverse(id)),
  })
}

// Generate and assign colours by hashing the IDs. The same ID will always result in the same color.
// The generated colours all have the same lightness and saturation values. Hues are generated from
// the IDs. The result is sorted by hue. Therefore, similar colours will be next to each other.
export const assignColorsSorted = (ids: string[]): ColorAssignment[] =>
  ids
    .map(id => (
      { id, color: colorFromId(id), }
    ))
    .sort((c1, c2) =>
      c1.color.hsl[0]! - c2.color.hsl[0]!
    )

// Copyright (c) 2023 WiseTime. All rights reserved.
// Check for color similarity by comparing each hue with the previous one in the sorted list.
// If a hue is too similar to its sibling, apply a saturation and optionally a lightness
// modifier to the color, to differentiate it further from its sibling.
export const modifySimilarColors = (colorAssignments: ColorAssignment[]): ColorAssignment[] => {
  for (let i = 1; i < colorAssignments.length; i++) {
    const prev = colorAssignments[i - 1]!
    const curr = colorAssignments[i]!
    const hueDelta = Math.abs(prev.color.hsl[0]! - curr.color.hsl[0]!)
    const noticeableDifference = 30

    if (hueDelta < noticeableDifference) {
      const prevSaturation = prev.color.hsl![1]!
      let modifiedSaturation = baselineSaturation
      let modifiedLightness = baselineLightness

      if (prevSaturation === baselineSaturation) {
        modifiedSaturation = baselineSaturation + 0.15
        modifiedLightness = baselineLightness + 0.02
      } else if (prevSaturation === baselineSaturation + 0.15) {
        modifiedSaturation = baselineSaturation - 0.1
      } else if (prevSaturation === baselineSaturation - 0.1) {
        modifiedSaturation = baselineSaturation
        modifiedLightness = baselineLightness - 0.05
      }

      curr.color = colorFromId(curr.id, {
        saturation: modifiedSaturation,
        lightness: modifiedLightness,
      })
    }
  }
  return colorAssignments
}
